//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { EmailTemplate } from '@apis/account-settings/emailSettingsApiModel';
import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { ProfileNetwork } from '@influencers/constants/influencersConstants';
import Cuid from '@models/Cuid';
import DateString from '@models/DateString';
import DraftId from '@models/DraftId';
import { InstagramPostType } from '@models/InstagramPostType';
import Like2BuyV2StoreId from '@models/Like2BuyV2StoreId';
import PinterestPinId from '@models/PinterestPinId';
import { PostDetailsPostType } from '@models/PostDetailsPostType';
import PostableId from '@models/PostableId';
import { SocialNetwork } from '@models/SocialNetwork';
import { PinterestScheduleType } from '@pinterest/redux/post-details/pinterestPostDetailsModel';
import { UniversalCalendarView } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { isDefined, isNotNull } from '@utils/filterPredicates';

import { RoutableStateName } from 'app/top-level-states';

/**
 * This is the master interface for all typed navigation routes in the dashboard.
 * Future state param interfaces should be added here, indexed by the full state
 * name that they pertain to.
 */
export interface IStateParams {
  'account-settings.social': {
    ['oauth-network']?: string;
    ['oauth-error']?: string;
  };

  'facebook.schedule': {
    targetDraftId?: DraftId;
    postType?: PostDetailsPostType.FacebookPageFeed;
    targetScheduledPostId?: PostableId;
    copiedScheduledPostId?: PostableId;
    copiedDraftId?: DraftId;
    postTimeMs?: number;
  };

  'influencers.list': {
    listId: number;
  };

  'instagramAccount.schedule.postDetails': {
    postTimeMs?: number;
    postType?: InstagramPostType;
    previousRouteName?: RoutableStateName;
    targetCuid?: Cuid;
    targetDraftPostId?: DraftId;
    targetScheduledPostId?: PostableId;
    copiedScheduledPostId?: PostableId;
    copiedDraftId?: DraftId;
  };

  'instagramAccount.like2buy-preview': {
    storeId: Like2BuyV2StoreId;
  };

  'listening.topics': {
    topicId?: string;
  };

  'youtube-oauth': {
    code?: string;
    error?: string;
    state?: string;
  };

  'twitter.schedule.postDetails': {
    postTimeMs?: number;
    scheduledPostId?: PostableId;
    targetCuid?: Cuid;
  };

  'twitterV2.schedule.postDetails': {
    draftPostId?: DraftId;
    postTimeMs?: number;
    postType?: PostDetailsPostType.TwitterPostingGroup;
    scheduledPostId?: PostableId;
    copiedScheduledPostId?: PostableId;
    copiedDraftId?: DraftId;
    targetCuid?: Cuid;
  };

  'pinterest.schedule.postDetails': {
    copiedPinId?: PinterestPinId;
    copiedScheduledPostId?: PostableId;
    copiedDraftId?: DraftId;
    draftPostId?: DraftId;
    postTimeMs?: number;
    postType?: PostDetailsPostType.PinterestOrganicPin;
    scheduleType?: PinterestScheduleType;
    scheduledPostId?: PostableId;
    targetCuids?: Cuid[];
  };

  'pinterest.schedule.select': {
    postTimeMs?: number;
    postType?: PostDetailsPostType.PinterestOrganicPin;
  };

  'tiktok.schedule.postDetails': {
    draftPostId?: DraftId;
    postTimeMs?: number;
    postType?: PostDetailsPostType.TikTokVideoAutomatic;
    scheduledPostId?: PostableId;
    copiedScheduledPostId?: PostableId;
    copiedDraftId?: DraftId;
    targetCuid?: Cuid;
  };

  'schedule.calendar': {
    startDate?: DateString;
    endDate?: DateString;
    view?: UniversalCalendarView;
  };

  'schedule.scheduler': {
    copiedDraftId?: DraftId;
    copiedScheduledPostId?: PostableId;
    draftPostId?: DraftId;
    postTimeMs?: number;
    postType?: UniversalPostType[];
    scheduledPostId?: PostableId;
    targetCuid?: Cuid;
    entryNetwork?: SocialNetwork;
  };

  'account-settings.creator-email-settings-edit': {
    emailTemplateId?: EmailTemplate['id'];
  };

  'account-settings.creator-email-settings-edit-v2': {
    emailTemplateId?: EmailTemplate['id'];
  };

  'campaigns.creators-manage-orders-details': {
    campaignSlugName: string;
    creatorUsername: string;
    orderId: string;
    network?: ProfileNetwork;
  };
}

export type TypedRoutableStateName = keyof IStateParams;

export type StateParams<T extends RoutableStateName> =
  T extends TypedRoutableStateName ? IStateParams[T] : never;

/**
 * Converts a given optional value to a string if it is not already a string or string-based.
 *
 * Ex --> StringifyValue<number> = string
 * Ex --> StringifyValue<InstagramPostType | undefined> = InstagramPostType | undefined
 */
type StringifyValue<T> = T extends string | undefined
  ? T | undefined
  : T extends string
    ? T
    : string;

/**
 * This helper type converts an interface with any value types to one that contains
 * only string-based types, while maintaining the optional status.
 *
 * Ex --> Stringify<{ val1: number, val2?: SomeEnum }> = { val1: string, val2?: SomeEnum }
 */
type Stringify<T extends Record<string, unknown>> = {
  [idx in keyof T]: StringifyValue<T[idx]>;
};

/**
 * State params are serialized into strings as they are passed into the URL,
 * so any number or number-based tinytype values will ultimately be outputted
 * as strings when we read them out of the state.
 *
 * The consumer of these types is then responsible for deserializing.
 */
export type StringifiedStateParams<T extends TypedRoutableStateName> =
  Stringify<StateParams<T>>;

/**
 * A helper to convert a string | undefined serialized query param
 * into a number, number-based tinytype, or undefined deserialized value.
 *
 * @param param The serialized param string
 */
export const deserializeOptionalNumericalParam = <T extends number>(
  param?: string | null,
): T | undefined => {
  return isDefined(param) && isNotNull(param)
    ? (parseInt(param, 10) as T)
    : undefined;
};

/**
 * A helper to convert a string | undefined serialized query param
 * into a boolean or undefined deserialized value.
 *
 * @param param The serialized param string
 */
export const deserializeOptionalBooleanParam = (
  param?: string | null,
): boolean | undefined => {
  if (isDefined(param) && isNotNull(param)) {
    switch (param.toLowerCase()) {
      case 'true':
        return true;
      case 'false':
        return false;
    }
  }

  return undefined;
};

/**
 * A helper to convert a string | undefined serialized query param
 * into a enum or undefined deserialized value.
 *
 * @param param The serialized param string
 */
export const deserializeOptionalEnumParam = <K extends string, E>(
  enumDef: { [key in K]: E },
  param?: string | null,
): E | undefined => {
  return isDefined(param) &&
    isNotNull(param) &&
    Object.values(enumDef).includes(param)
    ? (param as E)
    : undefined;
};
